import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../../../utils/api/deliveryDocumentation";
import { Button, Checkbox, Eye, Input, SearchCheckList, Tags } from "../../../ui";
import { ConfirmModal } from "../../../modals/Confirm";
import DisputeComposer from "./DisputeComposer";
import { useAppLabel } from "../../../../utils/useAppLabel";
import { AdminPagination, AdminStatus } from "../AdminUi";
import {
  canSelectForDispute,
  getDisputeBucket,
  requiresEvidenceOverride,
  requiresMatchOverride,
  summarizeSelection,
} from "./disputeState";
import {
  emailStatusLabel,
  objectionStatusLabel,
  objectionStatusVariant,
} from "./objectionStatus";

const GROUPS = [
  {
    key: "ready",
    title: "الجاهزة للاعتراض",
    tabTitle: "جاهزة للاعتراض",
    subtitle: "مطابقة تلقائية أو استثناء يدوي",
  },
  {
    key: "not-ready",
    title: "غير جاهزة للاعتراض",
    tabTitle: "غير جاهزة للاعتراض",
    subtitle: "غير مطابق أو تطابق متعدد قبل محاولة الاعتراض",
  },
  {
    key: "sent",
    title: "المعترض عنها",
    tabTitle: "المعترض عنها",
    subtitle: "تم إنشاء محاولة اعتراض سواء نجح البريد أو فشل",
  },
];

const DRILL_LEVELS = [
  "stores",
  "branches",
  "apps",
  "years",
  "months",
  "days",
  "compensations",
];
const DRILL_TITLES = {
  stores: "كل المطاعم",
  branches: "الفروع",
  apps: "التطبيقات",
  years: "السنوات",
  months: "الأشهر",
  days: "الأيام",
  compensations: "سجلات التعويض",
};
const DRILL_LABELS = {
  stores: "Store",
  branches: "Branch",
  apps: "App",
  years: "Year",
  months: "Month",
  days: "Day",
};
const ROOT_STACK = Object.freeze([{ level: "stores", label: "كل المطاعم" }]);

function stableParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== "" && value != null)
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? [...value].filter(Boolean).sort() : value,
      ])
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

function cacheKeyFor(params = {}) {
  return JSON.stringify(stableParams(params));
}

function text(value, fallback = "—") {
  return value == null || value === "" ? fallback : value;
}

function canSendObjection(item = {}) {
  return (
    ["draft", "generated", "failed"].includes(item.status) &&
    item.emailStatus !== "sent"
  );
}

function sendButtonLabel(item = {}, busy = false) {
  if (item.status === "sending" || busy) return "جارٍ الإرسال...";
  if (item.status === "failed" || item.emailStatus === "failed")
    return "إعادة محاولة الإرسال";
  if (item.status === "sent" || item.emailStatus === "sent")
    return "تم الإرسال";
  return "إرسال الاعتراض";
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = api.ensureExcelFilename(filename, "objection-report.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

function readinessReason(row) {
  if (row.matchStatus === "exceptioned") return "استثناء يدوي";
  if (row.matchStatus === "matched") return "مطابقة تلقائية";
  if (row.reviewReason?.code === "NO_MATCH") return "لا يوجد تطابق";
  if (
    row.matchStatus === "ambiguous" ||
    row.reviewReason?.code === "AMBIGUOUS_MATCH"
  )
    return "تطابق متعدد";
  if (row.reviewReason?.code === "MATCHED_ORDER_DELETED")
    return "طلب مطابق محذوف";
  return "";
}

function compensationDate(row) {
  const value =
    row.realDate ||
    row.sourceValues?.realDate ||
    row.sourceValues?.date ||
    row.matchedOrder?.businessDate;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function formatAmount(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("ar-SA", {
    style: "currency",
    currency: "SAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatDateTime(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString("ar-SA", { timeZone: "Asia/Riyadh" })
    : "—";
}

function formatArabicDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("ar-SA", {
        timeZone: "Asia/Riyadh",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
}

function truncateCause(value) {
  const cause = String(value || "").trim();
  return cause.length > 50 ? `${cause.slice(0, 50)}...` : cause || "—";
}

function stateBadges(row) {
  const badges = [];
  if (row.matchStatus === "matched")
    badges.push({ label: "مطابقة تلقائية", variant: "success" });
  else if (row.matchStatus === "exceptioned")
    badges.push({ label: "استثناء يدوي", variant: "warning" });
  else if (row.matchStatus === "ambiguous")
    badges.push({ label: "تطابق متعدد", variant: "danger" });
  else badges.push({ label: "غير مطابق", variant: "danger" });
  if (["matched", "exceptioned"].includes(row.matchStatus)) {
    badges.push({
      label:
        row.evidenceStatus === "complete" ? "الصور مكتملة" : "الصور غير مكتملة",
      variant: row.evidenceStatus === "complete" ? "success" : "warning",
    });
  }
  if (getDisputeBucket(row) === "submitted") {
    badges.push({
      label:
        row.emailStatus === "failed"
          ? "فشل الإرسال"
          : row.emailStatus === "pending"
            ? "جاري الإرسال"
            : "تم الإرسال",
      variant: row.emailStatus === "failed" ? "danger" : "success",
    });
  }
  return badges;
}

function dateKey(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toISOString().slice(0, 10)
    : null;
}

function monthLabel(month) {
  return String(month).padStart(2, "0");
}

function exceptionEligible(row) {
  return (
    getDisputeBucket(row) === "notReady" && !row.readinessException && !row.sent
  );
}

function disputeSelectable(row) {
  return canSelectForDispute(row);
}

// `appLabel` renders the app node title; node keys stay canonical app keys.
function groupRows(rows, appLabel) {
  const root = new Map();
  const ensure = (map, key, label, meta = {}) => {
    const safeKey = key || "unknown";
    if (!map.has(safeKey))
      map.set(safeKey, {
        key: safeKey,
        label: label || "غير محدد",
        count: 0,
        children: new Map(),
        rows: [],
        ...meta,
      });
    return map.get(safeKey);
  };
  rows.forEach((row) => {
    const date = compensationDate(row);
    const year = date ? date.getFullYear() : "unknown";
    const month = date ? date.getMonth() + 1 : "unknown";
    const day = date ? date.getDate() : "unknown";
    const matchedOrder =
      row.matchedDeliveryDocumentationOrder || row.matchedOrder || {};
    const storeId =
      row.normalized?.storeId ||
      matchedOrder.storeId ||
      row.storeId;
    const branchId = row.normalized?.branchId || matchedOrder.branchId;
    const applicationId =
      row.normalized?.applicationId || matchedOrder.applicationId;
    const applicationKey = row.normalized?.applicationKey;
    const storeLabel =
      row.storeName ||
      row.normalized?.restaurant ||
      matchedOrder.store ||
      "غير محدد";
    const branchLabel =
      row.branchName || row.normalized?.branch || matchedOrder.branch || "بدون فرع";
    const appTitle =
      appLabel(
        applicationKey,
        row.normalized?.applicationDisplayName,
        row.app,
        row.normalized?.application,
        matchedOrder.application,
      ) || "غير محدد";
    const storeKey =
      storeId || row.storeName || row.normalized?.restaurant || matchedOrder.store;
    const store = ensure(root, storeKey, storeLabel, { storeId });
    const branch = ensure(
      store.children,
      branchId || branchLabel,
      branchLabel,
      { branchId },
    );
    const app = ensure(
      branch.children,
      applicationKey || applicationId || appTitle,
      appTitle,
      { applicationId, applicationKey },
    );
    const yearNode = ensure(app.children, year, String(year), {
      year: Number(year),
    });
    const monthNode = ensure(
      yearNode.children,
      month,
      month === "unknown" ? "غير محدد" : monthLabel(month),
      {
        storeId: store.storeId,
        branchId: branch.branchId,
        applicationId: app.applicationId,
        year: Number(year),
        month: Number(month),
      },
    );
    const dayNode = ensure(
      monthNode.children,
      day,
      day === "unknown" ? "غير محدد" : String(day).padStart(2, "0"),
      { dayKey: dateKey(date) },
    );
    dayNode.rows.push(row);
    [store, branch, app, yearNode, monthNode, dayNode].forEach((node) => {
      node.count += 1;
    });
  });
  const sortNodes = (nodes) =>
    [...nodes.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), "ar", { numeric: true }),
    );
  return sortNodes(root);
}

function CompensationItem({
  row,
  exceptionMode,
  selected,
  onToggle,
  readyMode,
  readySelected,
  onReadyToggle,
}) {
  const orderId = text(
    row.orderId || row.normalized?.fullOrderNumber || row.matchedOrder?.orderId,
  );
  const cause = String(row.cause || "").trim();
  const selectable = !exceptionMode || exceptionEligible(row);
  const readySelectable = readyMode && disputeSelectable(row);
  return (
    <article className="admin-order dispute-order-card">
      <div className="admin-order-summary dispute-order-summary">
        {exceptionMode && (
          <Checkbox
            checked={selected.includes(row.id)}
            disabled={!selectable}
            onChange={() => onToggle(row)}
          />
        )}
        {readyMode && (
          <Checkbox
            checked={readySelected.includes(row.id)}
            disabled={!readySelectable}
            onChange={() => onReadyToggle(row)}
          />
        )}
        <div className="dispute-order-main">
          <div className="dispute-order-head">
            <div>
              <div className="admin-order-title">#{orderId}</div>
              <div className="admin-order-meta">
                Status: {text(row.orderStatus)}
              </div>
            </div>
            <div className="dispute-order-badges">
              {stateBadges(row).map((badge) => (
                <Tags
                  key={badge.label}
                  variant={badge.variant}
                  appearance="soft"
                  size="sm"
                >
                  {badge.label}
                </Tags>
              ))}
            </div>
          </div>
          <div className="dispute-amounts">
            <span>
              <b>Order Amount</b>
              {formatAmount(row.orderAmount)}
            </span>
            <span>
              <b>Refund Amount</b>
              {formatAmount(row.refundAmount)}
            </span>
            <span>
              <b>Endurance Amount</b>
              {formatAmount(row.enduranceAmount)}
            </span>
          </div>
          <div className="dispute-cause" title={cause || undefined}>
            <b>Cause</b>
            <span>{truncateCause(cause)}</span>
          </div>
          <div className="dispute-cause">
            <b>Readiness</b>
            <span>{readinessReason(row) || "—"}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

function DrillList({ level, items, onSelect }) {
  return (
    <div className="doc-drill-list">
      {items.map((item) => (
        <button
          className="doc-drill-item"
          type="button"
          key={`${level}-${item.key}`}
          onClick={() => onSelect(item)}
        >
          <div className="doc-drill-main">
            <span className="doc-drill-arrow">‹</span>
            <span className="doc-drill-name">
              <span className="doc-level-label">{DRILL_LABELS[level]}</span>
              {item.label}
            </span>
          </div>
          <div className="doc-drill-counts">
            <span className="doc-drill-count">{item.count} سجل</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function CompensationList({
  rows,
  exceptionMode,
  selected,
  onToggle,
  onToggleAll,
  readyMode,
  readySelected,
  onReadyToggle,
  onReadyToggleAll,
}) {
  if (!rows.length)
    return <div className="admin-empty">لا توجد سجلات تعويض في هذا اليوم.</div>;
  const eligibleRows = rows.filter(exceptionEligible);
  const selectedInView = eligibleRows.filter((row) =>
    selected.includes(row.id),
  ).length;
  const readyRows = rows.filter(disputeSelectable);
  const readySelectedInView = readyRows.filter((row) =>
    readySelected.includes(row.id),
  ).length;
  return (
    <>
      {readyMode && (
        <div className="admin-actions">
          <Button
            variant="neutral"
            appearance="soft"
            type="button"
            disabled={!readyRows.length}
            onClick={() => onReadyToggleAll(readyRows)}
          >
            {readySelectedInView === readyRows.length && readyRows.length
              ? "إلغاء تحديد الكل"
              : "تحديد كل اليوم"}
          </Button>
          <span className="admin-muted">
            {readySelectedInView} / {readyRows.length} محدد
          </span>
        </div>
      )}
      {exceptionMode && (
        <div className="admin-actions">
          <Button
            variant="neutral"
            appearance="soft"
            type="button"
            disabled={!eligibleRows.length}
            onClick={() => onToggleAll(eligibleRows)}
          >
            {selectedInView === eligibleRows.length && eligibleRows.length
              ? "إلغاء تحديد الكل"
              : "تحديد كل الشهر"}
          </Button>
          <span className="admin-muted">
            {selectedInView} / {eligibleRows.length} محدد
          </span>
        </div>
      )}
      <div className="admin-orders">
        {rows.map((row) => (
          <CompensationItem
            key={row.id}
            row={row}
            exceptionMode={exceptionMode}
            selected={selected}
            onToggle={onToggle}
            readyMode={readyMode}
            readySelected={readySelected}
            onReadyToggle={onReadyToggle}
          />
        ))}
      </div>
    </>
  );
}

function ObjectionSummary({ summary = {} }) {
  return (
    <div className="admin-analytics dispute-stats objection-summary">
      <div className="admin-stat">
        <span>إجمالي الاعتراضات</span>
        <b>{summary.total || 0}</b>
      </div>
      <div className="admin-stat">
        <span>تم الإرسال</span>
        <b>{summary.sent || 0}</b>
      </div>
      <div className="admin-stat">
        <span>فشل الإرسال</span>
        <b>{summary.failed || 0}</b>
      </div>
      <div className="admin-stat">
        <span>جارٍ الإرسال</span>
        <b>{summary.sending || 0}</b>
      </div>
      <div className="admin-stat">
        <span>بانتظار الإرسال</span>
        <b>{(summary.draft || 0) + (summary.generated || 0)}</b>
      </div>
    </div>
  );
}

function ObjectionRow({ item, onOpen, onSend, onDownload, retrying }) {
  const appLabel = useAppLabel();
  const fileUrl = item.generatedExcelFile?.url;
  const sendable = canSendObjection(item);
  return (
    <tr>
      <td>
        <b>{text(item.store)}</b>
        <span className="objection-cell-note">
          {text(item.branch, "بدون فرع")}
        </span>
      </td>
      <td>{text(appLabel(item.app))}</td>
      <td>
        {item.orderIds?.length ||
          item.generatedRecordCount ||
          item.reportRowIds?.length ||
          0}
      </td>
      <td>
        <AdminStatus status={objectionStatusVariant(item.status)}>
          {objectionStatusLabel(item.status)}
        </AdminStatus>
      </td>
      <td>
        <AdminStatus
          status={
            item.emailStatus === "failed"
              ? "rejected"
              : item.emailStatus === "sent"
                ? "done"
                : "pending"
          }
        >
          {emailStatusLabel(item.emailStatus)}
        </AdminStatus>
      </td>
      <td>{formatDateTime(item.creationDate)}</td>
      <td>
        {item.lastAttempt
          ? `${item.lastAttempt.attemptNumber} · ${formatDateTime(item.lastAttempt.attemptedAt)}`
          : "—"}
      </td>
      <td>{formatDateTime(item.sentAt)}</td>
      <td>
        <div className="objection-actions">
          {fileUrl && (
            <a
              className="admin-soft objection-link"
              href={fileUrl}
              onClick={(event) => {
                event.preventDefault();
                onDownload(item);
              }}
            >
              Excel
            </a>
          )}
          {sendable && (
            <Button
              type="button"
              variant="neutral"
              appearance="soft"
              size="sm"
              disabled={retrying}
              onClick={() => onSend(item)}
            >
              {sendButtonLabel(item, retrying)}
            </Button>
          )}
          {item.status === "sending" && (
            <span className="admin-muted">جارٍ الإرسال...</span>
          )}
          {(item.status === "sent" || item.emailStatus === "sent") && (
            <AdminStatus status="done">تم الإرسال</AdminStatus>
          )}
          <Button
            variant="neutral"
            appearance="text"
            size="sm"
            type="button"
            className="admin-inline-button"
            onClick={() => onOpen(item.id)}
            icon={<Eye size={13} />}
          />
        </div>
      </td>
    </tr>
  );
}

function ObjectionsList({ state, onOpen, onSend, onDownload, retryingId }) {
  if (state.loading)
    return <div className="admin-empty">جاري تحميل الاعتراضات...</div>;
  if (state.error)
    return <div className="admin-note danger">{state.error}</div>;
  if (!state.items.length)
    return (
      <div className="admin-empty">لا توجد اعتراضات ضمن الفلاتر المحددة</div>
    );
  return (
    <div className="admin-table-wrap dispute-table objection-table">
      <table>
        <thead>
          <tr>
            <th>المتجر / الفرع</th>
            <th>التطبيق</th>
            <th>عدد الطلبات</th>
            <th>الحالة</th>
            <th>البريد</th>
            <th>تاريخ الإنشاء</th>
            <th>آخر محاولة</th>
            <th>تاريخ الإرسال</th>
            <th>الاجراءات</th>
          </tr>
        </thead>
        <tbody>
          {state.items.map((item) => (
            <ObjectionRow
              key={item.id}
              item={item}
              onOpen={onOpen}
              onSend={onSend}
              onDownload={onDownload}
              retrying={retryingId === item.id}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObjectionDetails({
  details,
  loading,
  error,
  onClose,
  onSend,
  onDownload,
  retrying,
}) {
  const appLabel = useAppLabel();
  if (!details && !loading && !error) return null;
  const attempts = details?.attempts || [];
  return (
    <div className="matching-modal" role="dialog" aria-modal="true">
      <div className="matching-sheet objection-details">
        <div className="admin-details-top">
          <div>
            <h2>تفاصيل الاعتراض</h2>
            <p>{details?.id || "جاري التحميل..."}</p>
          </div>
          <Button
            variant="neutral"
            appearance="soft"
            type="button"
            onClick={onClose}
          >
            إغلاق
          </Button>
        </div>
        {loading && <div className="admin-empty">جاري تحميل التفاصيل...</div>}
        {error && <div className="admin-note danger">{error}</div>}
        {details && !loading && (
          <>
            <div className="admin-detail-grid">
              <div className="admin-detail-cell">
                <span>Store</span>
                <b>{text(details.store)}</b>
              </div>
              <div className="admin-detail-cell">
                <span>Branch</span>
                <b>{text(details.branch, "بدون فرع")}</b>
              </div>
              <div className="admin-detail-cell">
                <span>App</span>
                <b>{text(appLabel(details.app))}</b>
              </div>
              <div className="admin-detail-cell">
                <span>Created</span>
                <b>{formatDateTime(details.creationDate)}</b>
              </div>
              <div className="admin-detail-cell">
                <span>Status</span>
                <b>{objectionStatusLabel(details.status)}</b>
              </div>
              <div className="admin-detail-cell">
                <span>Email</span>
                <b>{emailStatusLabel(details.emailStatus)}</b>
              </div>
              <div className="admin-detail-cell">
                <span>Orders</span>
                <b>
                  {details.orderIds?.length ||
                    details.generatedRecordCount ||
                    0}
                </b>
              </div>
              <div className="admin-detail-cell">
                <span>Provider reference</span>
                <b>{text(details.providerReference)}</b>
              </div>
            </div>
            <div className="admin-section-title">Recipients</div>
            <div className="admin-note">
              To: {(details.to || []).join(", ") || "—"}
              <br />
              CC: {(details.cc || []).join(", ") || "—"}
            </div>
            <div className="admin-section-title">Subject</div>
            <div className="admin-note">{details.subject || "—"}</div>
            <div className="admin-actions">
              {details.generatedExcelFile?.url && (
                <a
                  className="admin-soft objection-link"
                  href={details.generatedExcelFile.url}
                  onClick={(event) => {
                    event.preventDefault();
                    onDownload(details);
                  }}
                >
                  فتح ملف Excel المولد
                </a>
              )}
              {canSendObjection(details) && (
                <Button
                  variant="neutral"
                  appearance="soft"
                  type="button"
                  disabled={retrying}
                  onClick={() => onSend(details)}
                >
                  {sendButtonLabel(details, retrying)}
                </Button>
              )}
              {(details.status === "sent" ||
                details.emailStatus === "sent") && (
                <AdminStatus status="done">تم الإرسال</AdminStatus>
              )}
            </div>
            <div className="admin-section-title">محاولات الإرسال</div>
            <div className="objection-attempts">
              {attempts.length ? (
                attempts.map((attempt) => (
                  <div
                    key={attempt.id || attempt.attemptNumber}
                    className="admin-note"
                  >
                    <b>
                      المحاولة {attempt.attemptNumber} ·{" "}
                      {emailStatusLabel(attempt.status)}
                    </b>
                    <span>
                      {formatDateTime(attempt.attemptedAt)} ·{" "}
                      {attempt.provider || "—"} ·{" "}
                      {(attempt.to || []).join(", ") ||
                        attempt.recipient ||
                        "—"}
                    </span>
                    {attempt.providerMessageId && (
                      <small>
                        provider reference: {attempt.providerMessageId}
                      </small>
                    )}
                    {attempt.error?.message && (
                      <small>{attempt.error.message}</small>
                    )}
                  </div>
                ))
              ) : (
                <div className="admin-empty">لا توجد محاولات إرسال بعد.</div>
              )}
            </div>
            {details.lastSendError?.message && (
              <>
                <div className="admin-section-title">Errors</div>
                <div className="admin-note danger">
                  {details.lastSendError.message}
                </div>
              </>
            )}
            <div className="admin-section-title">Evidence</div>
            <div className="objection-evidence-list">
              {(details.evidenceManifest || []).length ? (
                details.evidenceManifest.map((item) => (
                  <a
                    key={
                      item._id ||
                      `${item.documentationOrderId}-${item.evidenceType}`
                    }
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.evidenceType} ·{" "}
                    {item.originalName || item.documentationOrderId}
                  </a>
                ))
              ) : (
                <div className="admin-empty">
                  لا توجد روابط إثبات محفوظة في هذا الاعتراض.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function DisputeWorkspace({ importId = "" }) {
  const appLabel = useAppLabel();
  const [filters, setFilters] = useState({
    group: "ready",
    importId,
    restaurantIds: [],
    branchIds: [],
    apps: [],
    orderSearch: "",
    sort: "sourceRow_asc",
  });
  const [orderSearchInput, setOrderSearchInput] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    restaurants: [],
    branches: [],
    apps: [],
    loadingRestaurants: true,
    loadingBranches: false,
    loadingApps: true,
  });
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: "",
    items: [],
    counts: {},
    pagination: { page: 1 },
  });
  const [summaryState, setSummaryState] = useState({
    loading: true,
    refreshing: false,
    error: "",
    data: {},
  });
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [selected, setSelected] = useState([]);
  const [exceptionMode, setExceptionMode] = useState(false);
  const [exceptionSelected, setExceptionSelected] = useState([]);
  const [composerDispute, setComposerDispute] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detailsState, setDetailsState] = useState({
    loading: false,
    error: "",
    item: null,
  });
  const [retryingId, setRetryingId] = useState("");
  const [sendConfirm, setSendConfirm] = useState(null);
  const [createConfirm, setCreateConfirm] = useState(null);
  const [hierarchyStack, setHierarchyStack] = useState(ROOT_STACK);
  const loadRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);
  const listAbortRef = useRef(null);
  const summaryAbortRef = useRef(null);
  const listCacheRef = useRef(new Map());
  const summaryCacheRef = useRef(new Map());

  const invalidateCaches = useCallback((groups = []) => {
    const affected = new Set(groups);
    for (const key of listCacheRef.current.keys()) {
      try {
        const parsed = JSON.parse(key);
        if (!affected.size || affected.has(parsed.group)) {
          listCacheRef.current.delete(key);
        }
      } catch {
        listCacheRef.current.delete(key);
      }
    }
    summaryCacheRef.current.clear();
  }, []);

  useEffect(() => {
    setFilters((value) => ({ ...value, importId }));
  }, [importId]);

  useEffect(() => {
    let cancelled = false;
    async function loadReferenceOptions() {
      try {
        const [restaurants, apps] = await Promise.all([
          api.getAdminDisputeRestaurants(),
          api.getAdminDisputeApplications(),
        ]);
        if (cancelled) return;
        setFilterOptions((value) => ({
          ...value,
          restaurants: restaurants.items || [],
          apps: apps.items || [],
          loadingRestaurants: false,
          loadingApps: false,
        }));
      } catch {
        if (!cancelled) {
          setFilterOptions((value) => ({
            ...value,
            loadingRestaurants: false,
            loadingApps: false,
          }));
        }
      }
    }
    loadReferenceOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const restaurantKey = useMemo(
    () => [...filters.restaurantIds].sort().join(","),
    [filters.restaurantIds],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadBranches() {
      setFilterOptions((value) => ({ ...value, loadingBranches: true }));
      try {
        const data = await api.getAdminDisputeBranches({
          restaurantIds: restaurantKey,
        });
        if (cancelled) return;
        const branches = data.items || [];
        const valid = new Set(branches.map((item) => item.value));
        setFilterOptions((value) => ({
          ...value,
          branches,
          loadingBranches: false,
        }));
        setFilters((value) => ({
          ...value,
          branchIds: value.branchIds.filter((branchId) => valid.has(branchId)),
        }));
      } catch {
        if (!cancelled) {
          setFilterOptions((value) => ({ ...value, loadingBranches: false }));
        }
      }
    }
    loadBranches();
    return () => {
      cancelled = true;
    };
  }, [restaurantKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((value) => ({
        ...value,
        orderSearch: orderSearchInput.trim().replace(/\s+/g, ""),
      }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [orderSearchInput]);

  const summaryParams = useMemo(
    () =>
      stableParams({
        importId: filters.importId,
        restaurantIds: filters.restaurantIds,
        branchIds: filters.branchIds,
        apps: filters.apps,
        orderSearch: filters.orderSearch,
      }),
    [
      filters.importId,
      filters.restaurantIds,
      filters.branchIds,
      filters.apps,
      filters.orderSearch,
    ],
  );
  const summaryKey = useMemo(() => cacheKeyFor(summaryParams), [summaryParams]);

  const loadSummary = useCallback(async () => {
    const requestId = summaryRequestRef.current + 1;
    summaryRequestRef.current = requestId;
    summaryAbortRef.current?.abort();
    const controller = new AbortController();
    summaryAbortRef.current = controller;
    const cached = summaryCacheRef.current.get(summaryKey);
    setSummaryState((value) => ({
      ...value,
      loading: !cached,
      refreshing: Boolean(cached),
      error: "",
      data: cached || value.data || {},
    }));
    try {
      const data = await api.getAdminDisputeSummary({
        ...summaryParams,
        signal: controller.signal,
      });
      if (requestId !== summaryRequestRef.current) return;
      const summary = data.summary || {};
      summaryCacheRef.current.set(summaryKey, summary);
      setSummaryState({
        loading: false,
        refreshing: false,
        error: "",
        data: summary,
      });
    } catch (error) {
      if (error.name === "AbortError" || requestId !== summaryRequestRef.current)
        return;
      setSummaryState((value) => ({
        ...value,
        loading: false,
        refreshing: false,
        error: error.message,
      }));
    }
  }, [summaryKey, summaryParams]);

  const load = useCallback(
    async (page = 1, options = {}) => {
      const activeFilters = { ...filters, page, limit: 20 };
      const listKey = cacheKeyFor(activeFilters);
      const requestId = loadRequestRef.current + 1;
      loadRequestRef.current = requestId;
      listAbortRef.current?.abort();
      const controller = new AbortController();
      listAbortRef.current = controller;
      const cached = listCacheRef.current.get(listKey);
      setState((value) => ({
        ...value,
        ...(cached || {}),
        loading: !cached,
        refreshing: Boolean(cached),
        error: "",
        pagination: cached?.pagination || { ...value.pagination, page },
      }));
      try {
        const data =
          filters.group === "sent"
            ? await api.getAdminDisputes({
                page,
                limit: 20,
                restaurantIds: [...filters.restaurantIds].sort().join(","),
                branchIds: [...filters.branchIds].sort().join(","),
                apps: [...filters.apps].sort().join(","),
                orderSearch: filters.orderSearch || undefined,
                sortBy:
                  filters.sort === "updatedAt_asc"
                    ? "creationDate"
                    : "creationDate",
                sortDirection:
                  filters.sort === "updatedAt_asc" ||
                  filters.sort === "sourceRow_asc"
                    ? "asc"
                    : "desc",
                signal: controller.signal,
              })
            : await api.getAdminCompensationDisputeCandidates({
                ...activeFilters,
                signal: controller.signal,
              });
        if (requestId !== loadRequestRef.current) return;
        const nextState = {
          loading: false,
          refreshing: false,
          error: "",
          items: data.items || data.data || [],
          counts: data.counts || data.summary || {},
          pagination: data.pagination || {},
        };
        listCacheRef.current.set(listKey, nextState);
        setState(nextState);
        setSelected((current) =>
          current.filter((id) =>
            (data.items || []).some((row) => row.id === id),
          ),
        );
        setExceptionSelected((current) =>
          current.filter((id) =>
            (data.items || []).some((row) => row.id === id),
          ),
        );
      } catch (error) {
        if (error.name === "AbortError") return;
        if (requestId !== loadRequestRef.current) return;
        setState((value) => ({
          ...value,
          loading: false,
          refreshing: false,
          error: error.message,
        }));
      }
    },
    [filters],
  );

  async function openObjectionDetails(id) {
    setDetailsState({ loading: true, error: "", item: null });
    try {
      const data = await api.getAdminDisputeDetails(id);
      setDetailsState({ loading: false, error: "", item: data.objection });
    } catch (error) {
      setDetailsState({ loading: false, error: error.message, item: null });
    }
  }

  async function downloadObjectionExcel(item) {
    if (!item?.id) return;
    setFeedback({ type: "", message: "" });
    try {
      const result = await api.downloadGeneratedDisputeExcel(item.id);
      saveBlob(
        result.blob,
        result.filename ||
          item.generatedExcelFile?.originalName ||
          item.generatedExcelFile?.name ||
          "objection-report.xlsx",
      );
    } catch (error) {
      setFeedback({
        type: "error",
        message: error.message || "تعذر تحميل ملف Excel.",
      });
    }
  }

  function requestSendObjection(item) {
    if (!canSendObjection(item)) return;
    const retry = item.status === "failed" || item.emailStatus === "failed";
    setSendConfirm({
      id: item.id,
      retry,
      title: retry ? "تأكيد إعادة المحاولة" : "تأكيد إرسال الاعتراض",
      message: retry
        ? "فشلت محاولة الإرسال السابقة. سيتم إنشاء محاولة إرسال جديدة مع الاحتفاظ بسجل المحاولات السابقة.\n\nهل تريد إعادة المحاولة؟"
        : "سيتم إنشاء ملف الاعتراض وإرسال البريد الإلكتروني إلى الجهات المحددة في إعدادات التطبيق.\n\nهل أنت متأكد من رغبتك في الإرسال؟",
      confirmText: retry ? "إعادة المحاولة" : "إرسال",
    });
  }

  async function sendObjection(id, options = {}) {
    setRetryingId(id);
    setFeedback({ type: "", message: "" });
    try {
      const data = await api.sendAdminDispute(id, options);
      if (
        data.requiresConfirmation &&
        data.warningCode === "NO_DOCUMENTATION_ORDERS"
      ) {
        setSendConfirm({
          id,
          noDocumentationOrders: true,
          title: "إرسال الاعتراض بدون توثيق",
          message:
            "لا توجد طلبات توثيق مرتبطة بهذا الاعتراض.\n\nسيتم إرسال الاعتراض بدون صور أو مستندات مرتبطة بطلبات التوثيق.\n\nهل أنت متأكد من رغبتك في المتابعة؟",
          confirmText: "إرسال بدون توثيق",
          cancelText: "إلغاء",
          tone: "warning",
        });
        return;
      }
      setFeedback({
        type: data.dispute?.status === "failed" ? "error" : "success",
        message:
          data.dispute?.status === "failed"
            ? `فشل إرسال الاعتراض${data.dispute?.lastSendError?.message ? `: ${data.dispute.lastSendError.message}` : ". يمكنك إعادة المحاولة."}`
            : "تم إرسال الاعتراض وتحديث سجل المحاولات.",
      });
      invalidateCaches(["sent"]);
      await load(state.pagination.page || 1);
      await loadSummary();
      if (detailsState.item?.id === id) await openObjectionDetails(id);
    } catch (error) {
      setFeedback({
        type: "error",
        message: `فشل إرسال الاعتراض: ${error.message || "تعذر إرسال البريد الإلكتروني. يمكنك إعادة المحاولة."}`,
      });
    } finally {
      setRetryingId("");
    }
  }

  async function confirmSendObjection() {
    const confirm = sendConfirm;
    const id = confirm?.id;
    setSendConfirm(null);
    if (id)
      await sendObjection(
        id,
        confirm?.noDocumentationOrders
          ? { confirmWithoutDocumentationOrders: true }
          : {},
      );
  }

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(
    () => () => {
      listAbortRef.current?.abort();
      summaryAbortRef.current?.abort();
    },
    [],
  );

  function startExceptionMode() {
    setFilters((value) => ({ ...value, group: "not-ready" }));
    setSelected([]);
    setExceptionSelected([]);
    setExceptionMode(true);
    setFeedback({
      type: "success",
      message:
        "تم تفعيل وضع الاستثناء. افتح المتجر ثم الفرع ثم التطبيق ثم السنة ثم الشهر، وبعدها اختر السجلات المطلوبة.",
    });
  }

  function stopExceptionMode() {
    setExceptionMode(false);
    setExceptionSelected([]);
    setFeedback({ type: "", message: "" });
  }

  function toggleExceptionRow(row) {
    if (!exceptionEligible(row)) return;
    setExceptionSelected((current) =>
      current.includes(row.id)
        ? current.filter((id) => id !== row.id)
        : [...current, row.id],
    );
  }

  function toggleAllExceptionRows(rows) {
    const ids = rows.filter(exceptionEligible).map((row) => row.id);
    setExceptionSelected((current) => {
      const allSelected =
        ids.length > 0 && ids.every((rowId) => current.includes(rowId));
      return allSelected
        ? current.filter((rowId) => !ids.includes(rowId))
        : [...new Set([...current, ...ids])];
    });
  }

  async function saveScopeException() {
    if (!exceptionSelected.length) {
      setFeedback({ type: "error", message: "حدد سجل تعويض واحد على الأقل." });
      return;
    }
    setBusy(true);
    setFeedback({ type: "", message: "" });
    try {
      await api.createAdminDisputeReadinessException({
        rowIds: exceptionSelected,
      });
      setExceptionMode(false);
      setExceptionSelected([]);
      setFeedback({
        type: "success",
        message: "تم حفظ الاستثناء للسجلات المحددة فقط وتحديث جاهزيتها.",
      });
      invalidateCaches(["ready", "not-ready"]);
      await load(state.pagination.page || 1);
      await loadSummary();
    } catch (error) {
      setFeedback({ type: "error", message: error.message });
    } finally {
      setBusy(false);
    }
  }

  function toggleRow(row) {
    if (!disputeSelectable(row)) return;
    setSelected((current) =>
      current.includes(row.id)
        ? current.filter((id) => id !== row.id)
        : [...current, row.id],
    );
  }

  function toggleAllReadyRows(rows) {
    const ids = rows.filter(disputeSelectable).map((row) => row.id);
    setSelected((current) => {
      const allSelected =
        ids.length > 0 && ids.every((rowId) => current.includes(rowId));
      return allSelected
        ? current.filter((rowId) => !ids.includes(rowId))
        : [...new Set([...current, ...ids])];
    });
  }

  function createDraft() {
    const selectedRows = (state.items || []).filter((row) =>
      selected.includes(row.id),
    );
    const summary = summarizeSelection(selectedRows);
    const needsEvidenceOverride = selectedRows.some(requiresEvidenceOverride);
    const needsMatchOverride = selectedRows.some(requiresMatchOverride);
    if (needsEvidenceOverride || needsMatchOverride) {
      const lines = [
        `${summary.direct} يمكن إرسالها مباشرة`,
        `${summary.incompleteEvidence} صورها غير مكتملة`,
        `${summary.unmatched} غير مطابقة`,
        `${summary.ambiguous} لديها تطابق متعدد`,
        "",
        "هل تريد إرسال الاعتراض على أي حال؟",
      ];
      if (!window.confirm(lines.join("\n"))) {
        setFeedback({
          type: "error",
          message: "تم الإلغاء ولم يتم إنشاء محاولة اعتراض.",
        });
        return;
      }
    }
    setCreateConfirm({
      rowIds: selected,
      options: {
        evidenceOverride: needsEvidenceOverride,
        matchOverride: needsMatchOverride,
        allowNotReady: needsMatchOverride,
        confirmWithoutDocumentationOrders: true,
      },
      title: "تأكيد إنشاء الاعتراض",
      message:
        "سيتم إنشاء الاعتراض ومحاولة إرسال البريد الإلكتروني مباشرة.\nهل أنت متأكد؟",
      confirmText: "إنشاء وإرسال",
    });
  }

  async function executeCreateAndSend(confirm = createConfirm) {
    if (!confirm?.rowIds?.length) return;
    setCreateConfirm(null);
    setBusy(true);
    setFeedback({ type: "", message: "" });
    try {
      const data = await api.createAndSendAdminDispute(
        confirm.rowIds,
        confirm.options || {},
      );
      setComposerDispute(data.dispute);
      setSelected([]);
      setFeedback({
        type: data.emailFailed ? "error" : "success",
        message: data.emailFailed
          ? "تم إنشاء الاعتراض، ولكن تعذر إرسال البريد الإلكتروني. يمكن إعادة محاولة الإرسال."
          : "تم إنشاء الاعتراض وإرسال البريد الإلكتروني بنجاح",
      });
      invalidateCaches(["ready", "not-ready", "sent"]);
      await load(state.pagination.page || 1);
      await loadSummary();
    } catch (error) {
      const warnings = error.validationWarnings || [];
      if (error.code === "DISPUTE_VALIDATION_FAILED" && warnings.length) {
        const evidenceCount = warnings.filter(
          (warning) => warning.code === "INCOMPLETE_EVIDENCE",
        ).length;
        const matchCount = warnings.filter((warning) =>
          ["UNMATCHED", "AMBIGUOUS_MATCH"].includes(warning.code),
        ).length;
        const confirmed = window.confirm(
          `تحتاج العملية لتأكيد إضافي:\n${evidenceCount} صورها غير مكتملة\n${matchCount} غير مطابقة أو متعددة التطابق\n\nإرسال على أي حال؟`,
        );
        if (confirmed) {
          try {
            const data = await api.createAndSendAdminDispute(confirm.rowIds, {
              allowNotReady: matchCount > 0,
              matchOverride: matchCount > 0,
              evidenceOverride: evidenceCount > 0,
              confirmWithoutDocumentationOrders: true,
            });
            setComposerDispute(data.dispute);
            setSelected([]);
            setFeedback({
              type: data.emailFailed ? "error" : "success",
              message:
                data.emailFailed
                  ? "تم إنشاء الاعتراض بعد التأكيد، ولكن تعذر إرسال البريد الإلكتروني. يمكن إعادة محاولة الإرسال."
                  : "تم إنشاء الاعتراض وإرسال البريد الإلكتروني بنجاح",
            });
            invalidateCaches(["ready", "not-ready", "sent"]);
            await load(state.pagination.page || 1);
            await loadSummary();
          } catch (retryError) {
            setFeedback({ type: "error", message: retryError.message });
          }
        } else {
          setFeedback({
            type: "error",
            message: "تم إلغاء الإرسال ولم يتم إنشاء اعتراض.",
          });
        }
      } else {
        setFeedback({ type: "error", message: error.message });
      }
    } finally {
      setBusy(false);
    }
  }

  const counts = state.counts || {};
  const overallCounts = summaryState.data || {};
  const submittedTabCount =
    filters.group === "sent"
      ? state.pagination?.total || counts.total || 0
      : overallCounts.sent || counts.sent || 0;
  const current =
    GROUPS.find((item) => item.key === filters.group) || GROUPS[0];
  const hierarchy = useMemo(
    () => (filters.group === "sent" ? [] : groupRows(state.items || [], appLabel)),
    [filters.group, state.items, appLabel],
  );
  // Option values are canonical app keys from the API; labels follow the UI language.
  const appOptions = useMemo(
    () => filterOptions.apps.map((option) => ({ ...option, label: appLabel(option.value, option.label) })),
    [filterOptions.apps, appLabel],
  );
  const dateWindow = state.pagination?.dateWindow;
  const fetchedPeriodLabel =
    dateWindow?.from && dateWindow?.to
      ? `${formatArabicDate(dateWindow.from)} — ${formatArabicDate(dateWindow.to)}`
      : "";
  const hierarchyKey = useMemo(
    () =>
      JSON.stringify({
        group: filters.group,
        page: state.pagination?.page || 1,
        from: dateWindow?.from || "",
        to: dateWindow?.to || "",
        ids: (state.items || []).map((item) => item.id),
      }),
    [filters.group, state.pagination?.page, dateWindow, state.items],
  );
  const hierarchicalGroup = ["ready", "not-ready"].includes(filters.group);
  const drillCurrent = hierarchyStack[hierarchyStack.length - 1];
  const drillItems = useMemo(() => {
    if (drillCurrent.level === "stores") return hierarchy;
    if (drillCurrent.level === "compensations") return [];
    return [...(drillCurrent.node?.children?.values?.() || [])].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), "ar", { numeric: true }),
    );
  }, [drillCurrent, hierarchy]);
  const drillRows =
    drillCurrent.level === "compensations" ? drillCurrent.node?.rows || [] : [];
  const selectedRows = useMemo(
    () =>
      (state.items || []).filter((row) => exceptionSelected.includes(row.id)),
    [state.items, exceptionSelected],
  );
  const selectedScopeLabel = useMemo(() => {
    if (!selectedRows.length) return "";
    const date = compensationDate(selectedRows[0]);
    if (!date) return `${selectedRows.length} محدد`;
    return `${date.getFullYear()}-${monthLabel(date.getMonth() + 1)} · ${selectedRows.length} محدد`;
  }, [selectedRows]);

  useEffect(() => {
    setHierarchyStack(ROOT_STACK);
  }, [hierarchyKey]);

  const enterDrillNode = useCallback(
    (node) => {
      const nextLevel =
        DRILL_LEVELS[DRILL_LEVELS.indexOf(drillCurrent.level) + 1];
      setHierarchyStack((value) => [
        ...value,
        { level: nextLevel, label: node.label, node },
      ]);
    },
    [drillCurrent.level],
  );

  const setDrillCrumb = useCallback((index) => {
    setHierarchyStack((value) => value.slice(0, index + 1));
  }, []);

  return (
    <div className="dispute-workspace">
      <div className="admin-note">
        مصدر البيانات: صفوف الاستيراد المقبولة والمطابقات الحالية وحالة الطلب
        والصور الحالية. التاريخ والوقت للعرض والتنظيم فقط ولا يدخلان في التجميع.
      </div>
      <div className="admin-analytics dispute-stats">
        <div className="admin-stat">
          <span>إجمالي سجلات الأخطاء</span>
          <b>{summaryState.loading ? "..." : overallCounts.total || 0}</b>
        </div>
        <div className="admin-stat">
          <span>جاهزة للاعتراض</span>
          <b>{summaryState.loading ? "..." : overallCounts.ready || 0}</b>
        </div>
        <div className="admin-stat">
          <span>غير جاهزة للاعتراض</span>
          <b>{summaryState.loading ? "..." : overallCounts["not-ready"] || 0}</b>
        </div>
        <div className="admin-stat">
          <span>تم الاعتراض عنها</span>
          <b>{summaryState.loading ? "..." : overallCounts.sent || submittedTabCount}</b>
        </div>
      </div>
      {summaryState.error && (
        <div className="admin-note danger">{summaryState.error}</div>
      )}
      {filters.group === "sent" && <ObjectionSummary summary={counts} />}
      <section className="admin-filter-block">
        <div className="admin-panel-title">
          <h2>طلبات الاعتراض</h2>
          <span>ترقيم وفرز من الخادم</span>
        </div>
        <div className="admin-filters dispute-filters">
          <SearchCheckList
            label="Restaurant"
            options={filterOptions.restaurants}
            selected={filters.restaurantIds}
            onChange={(restaurantIds) =>
              setFilters((value) => ({ ...value, restaurantIds }))
            }
            placeholder={
              filterOptions.loadingRestaurants ? "جاري تحميل المطاعم..." : "Restaurant"
            }
            disabled={filterOptions.loadingRestaurants}
          />
          <SearchCheckList
            label="Branch"
            options={filterOptions.branches}
            selected={filters.branchIds}
            onChange={(branchIds) =>
              setFilters((value) => ({ ...value, branchIds }))
            }
            placeholder={
              filterOptions.loadingBranches ? "جاري تحميل الفروع..." : "Branch"
            }
            disabled={filterOptions.loadingBranches}
          />
          <SearchCheckList
            label="Application"
            options={appOptions}
            selected={filters.apps}
            onChange={(apps) => setFilters((value) => ({ ...value, apps }))}
            placeholder={
              filterOptions.loadingApps ? "جاري تحميل التطبيقات..." : "Application"
            }
            disabled={filterOptions.loadingApps}
          />
          <Input
            label="رقم الطلب أو آخر 4 أرقام"
            fullWidth={false}
            value={orderSearchInput}
            inputMode="numeric"
            onChange={(event) => setOrderSearchInput(event.target.value)}
            placeholder="رقم الطلب أو آخر 4 أرقام"
          />
        </div>
        <div className="status-tabs dispute-status-tabs">
          {GROUPS.map((group) => (
            <button
              key={group.key}
              type="button"
              className={filters.group === group.key ? "on" : ""}
              onClick={() =>
                setFilters((value) => ({ ...value, group: group.key }))
              }
            >
              <b>{group.tabTitle}</b>
              <span>{group.subtitle}</span>
              <strong>
                {group.key === "sent"
                  ? overallCounts.sent || submittedTabCount
                  : overallCounts[group.key] || counts[group.key] || 0}
              </strong>
            </button>
          ))}
        </div>
        <div className="admin-actions">
          {selected.length > 0 && ["ready"].includes(filters.group) && (
            <Button
              variant="neutral"
              appearance="soft"
              type="button"
              disabled={filters.group === "sent" || !selected.length || busy}
              onClick={createDraft}
            >
              {busy && selected.length
                ? "جاري إنشاء وإرسال الاعتراض..."
                : `إنشاء اعتراض (${selected.length})`}
            </Button>
          )}
          {!exceptionMode && ["not-ready"].includes(filters.group) && (
            <Button
              variant="neutral"
              appearance="soft"
              type="button"
              disabled={filters.group === "sent" || busy}
              onClick={startExceptionMode}
            >
              Make Exception
            </Button>
          )}
          {exceptionMode && ["not-ready"].includes(filters.group) && (
            <Button
              variant="warning"
              appearance="soft"
              type="button"
              disabled={busy || !exceptionSelected.length}
              onClick={saveScopeException}
            >
              {busy
                ? "جاري الحفظ..."
                : `تطبيق الاستثناء (${exceptionSelected.length})`}
            </Button>
          )}
          {exceptionMode && ["not-ready"].includes(filters.group) && (
            <Button
              variant="neutral"
              appearance="soft"
              type="button"
              disabled={busy}
              onClick={stopExceptionMode}
            >
              إلغاء وضع الاستثناء
            </Button>
          )}
          <Button
            variant="danger"
            appearance="soft"
            type="button"
            onClick={() => {
              setOrderSearchInput("");
              setFilters({
                group: "ready",
                importId,
                restaurantIds: [],
                branchIds: [],
                apps: [],
                orderSearch: "",
                sort: "sourceRow_asc",
              });
            }}
          >
            مسح الفلاتر
          </Button>
          {selected.length > 0 && (
            <Button
              variant="danger"
              appearance="soft"
              type="button"
              disabled={!selected.length}
              onClick={() => setSelected([])}
            >
              إلغاء التحديد
            </Button>
          )}
        </div>
        {feedback.message && (
          <div className={`admin-export-feedback ${feedback.type}`}>
            {feedback.message}
          </div>
        )}
      </section>
      <div className="admin-record-header">
        <div>
          <b>{current.title}</b>
          <span>{current.subtitle}</span>
        </div>
        {hierarchicalGroup && dateWindow && (
          <div>
            <b>الفترة</b>
            <span>{fetchedPeriodLabel || `${dateWindow.from} → ${dateWindow.to}`}</span>
          </div>
        )}
        {exceptionMode && (
          <div>
            <b>وضع الاستثناء</b>
            <span>
              {selectedScopeLabel || "افتح شهرًا وحدد السجلات المطلوبة"}
            </span>
          </div>
        )}
      </div>
      {filters.group === "sent" ? (
        <ObjectionsList
          state={state}
          onOpen={openObjectionDetails}
          onSend={requestSendObjection}
          onDownload={downloadObjectionExcel}
          retryingId={retryingId}
        />
      ) : (
        hierarchicalGroup && (
          <div className="doc-drill-shell dispute-hierarchy">
            <div className="doc-drill-top">
              <div className="doc-crumbs">
                {hierarchyStack.map((item, index) => (
                  <button
                    className={`doc-crumb ${index === hierarchyStack.length - 1 ? "current" : ""}`}
                    type="button"
                    key={`${item.level}-${index}`}
                    onClick={() => setDrillCrumb(index)}
                  >
                    {item.label}
                  </button>
                ))}
                {dateWindow && (
                  <span className="doc-crumb current">
                    الفترة: {fetchedPeriodLabel || `${dateWindow.from} → ${dateWindow.to}`}
                  </span>
                )}
              </div>
              <div className="doc-drill-title">
                {DRILL_TITLES[drillCurrent.level]}
              </div>
            </div>
            {state.loading && (
              <div className="admin-empty">جاري تحميل طلبات الاعتراض...</div>
            )}
            {state.error && (
              <div className="admin-note danger">{state.error}</div>
            )}
            {!state.loading &&
              !state.error &&
              drillCurrent.level !== "compensations" &&
              Boolean(drillItems.length) && (
                <DrillList
                  level={drillCurrent.level}
                  items={drillItems}
                  onSelect={enterDrillNode}
                />
              )}
            {!state.loading &&
              !state.error &&
              drillCurrent.level === "compensations" && (
                <CompensationList
                  rows={drillRows}
                  exceptionMode={
                    exceptionMode && !["ready"].includes(filters.group)
                  }
                  selected={exceptionSelected}
                  onToggle={toggleExceptionRow}
                  onToggleAll={toggleAllExceptionRows}
                  readyMode={["ready"].includes(filters.group)}
                  readySelected={selected}
                  onReadyToggle={toggleRow}
                  onReadyToggleAll={toggleAllReadyRows}
                />
              )}
            {!state.loading &&
              !state.error &&
              drillCurrent.level !== "compensations" &&
              !drillItems.length && (
                <div className="admin-empty">
                  لا توجد سجلات تعويض داخل نافذة التاريخ الحالية.
                </div>
              )}
          </div>
        )
      )}
      <AdminPagination
        page={state.pagination.page}
        totalPages={state.pagination.totalPages}
        hasPreviousPage={state.pagination.hasPreviousPage}
        hasNextPage={state.pagination.hasNextPage}
        label={
          hierarchicalGroup ? `نافذة ${state.pagination.page || 1}` : undefined
        }
        onPrevious={() => load(state.pagination.page - 1)}
        onNext={() => load(state.pagination.page + 1)}
      />
      <DisputeComposer
        initialDispute={composerDispute}
        onChange={setComposerDispute}
        onClose={() => setComposerDispute(null)}
      />
      <ObjectionDetails
        details={detailsState.item}
        loading={detailsState.loading}
        error={detailsState.error}
        onClose={() =>
          setDetailsState({ loading: false, error: "", item: null })
        }
        onSend={requestSendObjection}
        onDownload={downloadObjectionExcel}
        retrying={Boolean(retryingId)}
      />
      {sendConfirm && (
        <ConfirmModal
          title={sendConfirm.title}
          message={sendConfirm.message}
          confirmText={sendConfirm.confirmText}
          cancelText={sendConfirm.cancelText || "إلغاء"}
          tone={sendConfirm.tone}
          onConfirm={confirmSendObjection}
          onCancel={() => setSendConfirm(null)}
        />
      )}
      {createConfirm && (
        <ConfirmModal
          title={createConfirm.title}
          message={createConfirm.message}
          confirmText={createConfirm.confirmText}
          cancelText="إلغاء"
          tone={createConfirm.tone}
          onConfirm={() => executeCreateAndSend(createConfirm)}
          onCancel={() => setCreateConfirm(null)}
        />
      )}
    </div>
  );
}
